import axios from "axios";
import {
  Filter,
  CrudBuilderParams,
  GetParams,
  GetParamsWithSpace,
  PutParams,
  PutManyParams,
  DeleteManyParams,
  PostParams,
  DeleteParams,
  UploadParams,
  CrudBuilderReturnType,
  Response,
  Query,
  GetAllSettings,
} from "./types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const filterOutRecord = (rule: Filter) => {
  const { record = "", ...restOfFilter } = rule;
  return restOfFilter;
};

const convertDateFiltersToUTC = (rule: Filter) => {
  const { type, value } = rule;

  if (type !== "date") return rule;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateTimezone = dayjs.tz(value as string, timezone);

  const dateInUTC = dateTimezone.utc().toISOString();

  return { ...rule, value: dateInUTC };
};

/* eslint-disable */
export default <T extends string, P extends boolean = false>({ routes, spaceIsOptional }: CrudBuilderParams<T, P>): CrudBuilderReturnType<T, P> => {
  const get = async (params: P extends true ? GetParams<T> : GetParamsWithSpace<T>): Promise<any> => {
    const { getAll, ...genericParams } = params;
    let { queries, filters, ids, space, abortController, isSuperAdmin } = genericParams;

    let url: string = routes.reduce((allRoutesWithIds, route) => {
      allRoutesWithIds += `/${route}`;

      if (ids && ids[route]) {
        if (Array.isArray(ids[route])) {
          // @ts-ignore - we already did a conditional to verify ids[route] is not undefined
          ids[route].forEach((id) => (allRoutesWithIds += `/${id}`));
        } else {
          allRoutesWithIds += `/${ids[route]}`;
        }
      }

      return allRoutesWithIds;
    }, "/api");

    // validate we have the space filter if it is not part of exceptions - check childRoute if there is one rather than regular route
    if (!spaceIsOptional && !space && !isSuperAdmin) {
      throw `Route "${url}" must be filtered by space but no space parameter is present`;
    }

    let page = getAll?.page || 1;
    let accumulatedRecords = getAll?.accumulatedRecords || [];
    let responseData = getAll?.responseData || "";

    if (queries || getAll) {
      let queryList: string[] = [];

      if (queries) {
        Object.keys(queries).forEach((key: string) => {
          // when doing get all - ignore limit and page queries
          if (getAll && ["limit", "page"].includes(key)) return;

          // Type assertion on "as Query" because we are already doing if(queries) to make sure it is not undefined but still getting TS error
          queryList.push(`${key}=${(queries as Query)[key]}`);
        });
      }

      if (getAll) {
        queryList.push(`limit=1000`);
        queryList.push(`page=${page}`);
      }

      url += `?${queryList.join("&")}`;
    }

    if (filters || space) {
      if (!filters) {
        filters = {
          rules: {
            and: [],
            or: [],
          },
        };
      }
      if (!filters.rules.and) {
        filters.rules.and = [];
      }
      if (!filters.rules.or) {
        filters.rules.or = [];
      }

      if (space && !getAll?.page) {
        filters.rules.and.push({
          field: "space",
          operator: "equals",
          value: space,
          type: "ObjectId",
        });
      }

      filters.rules = {
        and: filters.rules.and.map(filterOutRecord).map(convertDateFiltersToUTC),
        or: filters.rules.or.map(filterOutRecord).map(convertDateFiltersToUTC),
      };

      const querySymbol = url.includes("?") ? "&" : "?";
      url += `${querySymbol}filters=${encodeURIComponent(JSON.stringify(filters))}`;
    }

    const response: Response = await axios.get(url, {
      signal: abortController?.signal,
    });

    const records = response.data;

    if (getAll) {
      accumulatedRecords.push(...records[responseData]);

      // if more records in the collection than what we have retrieved so far, retrieve the records starting at the next page
      if (accumulatedRecords.length < records.totalRecords) {
        page++;

        const getAllSettings: GetAllSettings = {
          page,
          responseData,
          accumulatedRecords,
        };

        const getSettings: P extends true ? GetParams<T> : GetParamsWithSpace<T> = {
          ...params,
          getAll: getAllSettings,
        };

        return await get(getSettings);
      }

      return {
        [responseData]: accumulatedRecords,
        totalRecords: records.totalRecords,
      };
    }

    return response.data;
  };

  const put = async ({ formBody, queries, ids, bulk }: PutParams<T>) => {
    let url: string = routes.reduce((allRoutesWithIds, route) => {
      allRoutesWithIds += `/${route}`;

      if (ids && ids[route]) {
        if (Array.isArray(ids[route])) {
          // @ts-ignore - we already did a conditional to verify ids[route] is not undefined
          ids[route].forEach((id) => (allRoutesWithIds += `/${id}`));
        } else {
          allRoutesWithIds += `/${ids[route]}`;
        }
      }

      return allRoutesWithIds;
    }, "/api");

    if (queries) {
      let queryList: string[] = [];
      Object.keys(queries).forEach((key: string) => {
        queryList.push(`${key}=${queries[key]}`);
      });
      url += `?${queryList.join("&")}`;
    }

    const { lastModified, ...validFormBody } = formBody;

    const response: Response = await axios({
      method: "PUT",
      url,
      data: validFormBody,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  };

  const putMany = async ({ bulkUpdateBody, ids, putIds, data }: PutManyParams<T>) => {
    let url: string = routes.reduce((allRoutesWithIds, route) => {
      allRoutesWithIds += `/${route}`;

      if (ids && ids[route]) {
        if (Array.isArray(ids[route])) {
          // @ts-ignore - we already did a conditional to verify ids[route] is not undefined
          ids[route].forEach((id) => (allRoutesWithIds += `/${id}`));
        } else {
          allRoutesWithIds += `/${ids[route]}`;
        }
      }

      return allRoutesWithIds;
    }, "/api");

    const { lastModified, ...validFormBody } = bulkUpdateBody;

    const body = {
      ids: putIds,
      bulkUpdateBody: validFormBody,
      data,
    };

    const response: Response = await axios({
      method: "PUT",
      url,
      data: body,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  };

  const deleteMany = async ({ ids, deleteIds, queries }: DeleteManyParams<T>) => {
    let url: string = routes.reduce((allRoutesWithIds, route) => {
      allRoutesWithIds += `/${route}`;

      if (ids && ids[route]) {
        if (Array.isArray(ids[route])) {
          // @ts-ignore - we already did a conditional to verify ids[route] is not undefined
          ids[route].forEach((id) => (allRoutesWithIds += `/${id}`));
        } else {
          allRoutesWithIds += `/${ids[route]}`;
        }
      }

      return allRoutesWithIds;
    }, "/api");

    url += `?ids=${deleteIds}`;

    if (queries) {
      let queryList: string[] = [];
      Object.keys(queries).forEach((key: string) => {
        queryList.push(`${key}=${queries[key]}`);
      });
      url += `&${queryList.join("&")}`;
    }

    const response: Response = await axios({
      url,
      method: "DELETE",
    });

    return response.data;
  };

  const post = async ({ formBody, ids }: PostParams<T>) => {
    let url: string = routes.reduce((allRoutesWithIds, route, i) => {
      allRoutesWithIds += `/${route}`;

      if (ids && ids[route]) {
        if (Array.isArray(ids[route])) {
          // @ts-ignore - we already did a conditional to verify ids[route] is not undefined
          ids[route].forEach((id) => (allRoutesWithIds += `/${id}`));
        } else {
          allRoutesWithIds += `/${ids[route]}`;
        }
      }

      return allRoutesWithIds;
    }, "/api");

    const response: Response = await axios({
      method: "POST",
      url,
      data: formBody,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  };

  const remove = async ({ ids }: DeleteParams<T>) => {
    let url: string = routes.reduce((allRoutesWithIds, route, i) => {
      allRoutesWithIds += `/${route}`;

      if (ids && ids[route]) {
        if (Array.isArray(ids[route])) {
          // @ts-ignore - we already did a conditional to verify ids[route] is not undefined
          ids[route].forEach((id) => (allRoutesWithIds += `/${id}`));
        } else {
          allRoutesWithIds += `/${ids[route]}`;
        }
      }

      return allRoutesWithIds;
    }, "/api");

    const response: Response = await axios({
      method: "DELETE",
      url,
    });

    return response.data;
  };

  const upload = async ({ parsedData, space, upload, ids }: UploadParams<T>) => {
    let url: string = routes.reduce((allRoutesWithIds, route, i) => {
      allRoutesWithIds += `/${route}`;

      if (ids && ids[route]) {
        if (Array.isArray(ids[route])) {
          // @ts-ignore - we already did a conditional to verify ids[route] is not undefined
          ids[route].forEach((id) => (allRoutesWithIds += `/${id}`));
        } else {
          allRoutesWithIds += `/${ids[route]}`;
        }
      }

      return allRoutesWithIds;
    }, "/api");

    url += "/upload";

    const response: Response = await axios({
      method: "POST",
      url,
      headers: {
        "Content-Type": "application/json",
      },
      data: { parsedData, space, upload },
    });

    return response.data;
  };

  return {
    get,
    put,
    putMany,
    delete: remove,
    deleteMany,
    post,
    upload,
  };
};
